import { RoundedCta } from "~/components/ctas";
import { Arrow } from "~/components/icons";
import clsx from "clsx";
import { FormEventHandler, useEffect } from "react";
import { Form } from "@remix-run/react";

interface NewsletterFormProps {
  onSubmit: FormEventHandler;
}

interface Mapping {
  FormFieldName: string;
  DataverseFieldName?: string;
  DataverseFieldValue?:
    | Array<{ FormValue: string; DataverseValue: string }>
    | string;
}

interface CaptureConfig {
  FormId: string;
  FormApiUrl: string;
}
// Define the mappings
const mappings: Mapping[] = [
  {
    FormFieldName: "newsletter_first_name",
    DataverseFieldName: "firstname",
  },
  {
    FormFieldName: "newsletter_last_name",
    DataverseFieldName: "lastname",
  },
  {
    FormFieldName: "newsletter_email",
    DataverseFieldName: "emailaddress1",
  },
  {
    FormFieldName: "newsletter_contact_type",
    DataverseFieldName: "c9_contacttype",
    DataverseFieldValue: [
      { FormValue: "public", DataverseValue: "794540000" }, // Public
      { FormValue: "", DataverseValue: "794540001" }, // Stakeholder
    ],
  },
  {
    FormFieldName: "page_signed_up_on",
    DataverseFieldName: "tbi_sourcewebpage",
  },
  {
    FormFieldName: "newsletter_consent",
    DataverseFieldName: "msdynmkt_topicid;channels;optinwhenchecked",
    DataverseFieldValue: "ef597b17-17ee-ef11-9341-6045bdc1cb95;Email;true",
  },
  {
    FormFieldName: "newsletter_emailoptin",
    DataverseFieldName: "tbi_emailoptin",
    DataverseFieldValue: [
      { FormValue: "yes", DataverseValue: "1" }, // Yes
      { FormValue: "", DataverseValue: "0" }, // No
    ],
  },
];

const captureConfig: CaptureConfig = {
  FormId: "97885501-d72c-f011-8c4d-7c1e5265c85e",
  FormApiUrl:
    "https://public-gbr.mkt.dynamics.com/api/v1.0/orgs/7a2dad3e-a1b3-4828-98dd-e6bd8c664cde/landingpageforms",
};

// this one??
export default function NewsletterForm({ onSubmit }: NewsletterFormProps) {
  const handleSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    onSubmit(e);
    if (!window.d365mktformcapture) {
      return;
    }

    const form = e.currentTarget as HTMLFormElement;
    const serializedForm = window.d365mktformcapture.serializeForm(
      form,
      mappings
    );
    const payload = serializedForm.SerializedForm.build();
    try {
      await window.d365mktformcapture.submitForm(captureConfig, payload);
    } catch (e) {
      console.log(e, "error");
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/gbr/FormCapture/FormCapture.bundle.js`;
    script.async = true;

    script.onload = () => {
      const interval = setInterval(() => {
        if (window.d365mktformcapture) {
          clearInterval(interval);
        }
      }, 100);

      setTimeout(() => clearInterval(interval), 5000); // stop trying after 5s
    };
    script.onerror = () => {
      console.error("Failed to load FormCapture script");
    };
    document.body.appendChild(script);
  }, []);

  return (
    <Form
      className="mb-16 h-full w-full"
      onSubmit={handleSubmit}
      id="newsletter_form"
    >
      <h5 className="mb-10">
        Be the first to know what we're doing - and how you can get more
        involved.
      </h5>
      <label htmlFor="newsletter_first_name" className="flex flex-col-reverse">
        <input
          id="newsletter_first_name"
          name="newsletter_first_name"
          autoFocus={true}
          type={"text"}
          required
          defaultValue=""
          placeholder="First name *"
          className={clsx(
            "body border-b-1 col-span-4 mb-6 w-full border-0 border-b border-solid border-b-black py-1 text-black outline-0 outline-offset-4 outline-slate placeholder:text-secondary_text focus:outline-1 lg:col-start-2"
          )}
        />
        <span className="meta-small text-secondary_text opacity-0 transition-opacity peer-focus:opacity-100">
          First Name *
        </span>
      </label>
      <label htmlFor="newsletter_last_name" className="flex flex-col-reverse">
        <input
          id="newsletter_last_name"
          name="newsletter_last_name"
          autoFocus={true}
          type={"text"}
          required
          defaultValue=""
          placeholder="Last name *"
          className={clsx(
            "body border-b-1 col-span-4 mb-6 w-full border-0 border-b border-solid border-b-black py-1 text-black outline-0 outline-offset-4 outline-slate placeholder:text-secondary_text focus:outline-1 lg:col-start-2"
          )}
        />
        <span className="meta-small text-secondary_text opacity-0 transition-opacity peer-focus:opacity-100">
          Last Name *
        </span>
      </label>
      <input
        type={"email"}
        id="newsletter_email"
        name="newsletter_email"
        className="body border-b-1 col-span-4 mb-6 w-full border-0 border-b border-solid border-b-black py-1 text-black outline-0 outline-offset-4 outline-slate placeholder:text-secondary_text focus:outline-1 lg:col-start-2"
        defaultValue=""
        placeholder="Email *"
        required
      />
      <select
        id="newsletter_contact_type"
        name="newsletter_contact_type"
        className="hidden"
        required
      >
        <option selected>public</option>
      </select>
      <input
        type="hidden"
        name="page_signed_up_on"
        value={typeof window !== "undefined" ? window.location.href : ""}
        readOnly
      />
      <select
        id="newsletter_emailoptin"
        name="newsletter_emailoptin"
        className="hidden"
        required
      >
        <option selected>yes</option>
      </select>
      <div className="my-10 flex items-start">
        <input
          type={"checkbox"}
          id="newsletter_consent"
          name="newsletter_consent"
          className="mr-3 border border-solid border-black"
          required
        />
        <label htmlFor="newsletter_consent" className="meta-small">
          Yes, I agree to receive emails about the work of Tony Blair and the
          Tony Blair Institute. For full information on the use of your data
          please see our privacy policy.
        </label>
      </div>
      <RoundedCta kind="black" label="Sign Up" type="submit" icon={<Arrow />} />
    </Form>
  );
}
